import React, { useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Mail, Phone, Facebook, Instagram, Linkedin, Youtube, ArrowUpRight } from 'lucide-react';

const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const socialLinks = [
  { href: 'https://www.facebook.com/yunlenn/', label: 'Facebook', icon: Facebook },
  { href: 'https://x.com/yunlenapp', label: 'X', icon: XIcon },
  { href: 'https://www.instagram.com/yunlen.app/', label: 'Instagram', icon: Instagram },
  { href: 'https://linkedin.com/company/yunlen', label: 'LinkedIn', icon: Linkedin },
  { href: 'https://www.youtube.com/channel/UCrFr0VuNM8Afp5emyAflT4g', label: 'YouTube', icon: Youtube },
  { href: 'https://www.tiktok.com/@yunlenapp', label: 'TikTok', icon: TikTokIcon },
];

const columns: { title: string; links: { to: string; label: string }[] }[] = [
  {
    title: 'Products',
    links: [
      { to: '/accounts', label: 'Accounts' },
      { to: '/stablecoin-wallet', label: 'Stablecoin Wallet' },
      { to: '/cards', label: 'Yunlen Card' },
      { to: '/downloadapp', label: 'Download App' },
    ],
  },
  {
    title: 'Company',
    links: [
      { to: '/about', label: 'About' },
      { to: '/blog', label: 'Blog' },
      { to: '/faq', label: 'FAQ' },
      { to: '/contact', label: 'Contact' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { to: '/terms', label: 'Terms of Service' },
      { to: '/privacy', label: 'Privacy Policy' },
    ],
  },
];

const FooterLink: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => (
  <NavLink
    to={to}
    end
    className={({ isActive }) =>
      `aug-link aug-body py-2 ${isActive ? 'text-[var(--color-accent)]' : 'text-[var(--theme-muted)]'}`
    }
  >
    {children}
  </NavLink>
);

/**
 * Sticky-reveal footer.
 *
 * The footer is fixed behind the page content, so the last section slides up
 * and uncovers it — the `--footer-reveal` behaviour on the reference site. The
 * effect is only switched on when the footer actually fits in the viewport;
 * otherwise it stays in normal flow so nothing is ever cut off.
 */
const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = document.documentElement;

    const sync = () => {
      const h = el.offsetHeight;
      root.style.setProperty('--site-footer-h', `${h}px`);
      // Taller than the viewport would mean part of it could never be reached.
      root.setAttribute('data-footer-reveal', h <= window.innerHeight * 0.9 ? 'on' : 'off');
    };

    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    window.addEventListener('resize', sync);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', sync);
      root.removeAttribute('data-footer-reveal');
      root.style.removeProperty('--site-footer-h');
    };
  }, []);

  return (
    <footer
      ref={ref}
      data-theme="dark"
      className="site-footer aug-theme aug-grain overflow-hidden"
    >
      <div className="mx-auto w-full max-w-[1400px] px-5 sm:px-8 lg:px-12">
        {/* Link grid */}
        <div className="grid gap-x-10 gap-y-10 lg:grid-cols-[1.4fr_repeat(3,minmax(0,0.75fr))] pt-14 sm:pt-20 pb-10">
          {/* Brand */}
          <div>
            <Link to="/" className="inline-flex items-center gap-3 group" aria-label="Yunlen home">
              <span className="w-10 h-10 rounded-xl bg-[var(--color-accent)] flex items-center justify-center overflow-hidden transition-transform duration-300 group-hover:rotate-[-8deg]">
                <img src="/icon-192.png" alt="" className="w-6 h-6 object-contain" />
              </span>
              <span className="font-extrabold tracking-[-0.04em] text-xl leading-none">YUNLEN</span>
            </Link>

            <p className="aug-body mt-6 max-w-sm text-[var(--theme-muted)]">
              Virtual payment accounts for the world. Access USD, EUR, GBP and AED accounts with
              stablecoin settlement, designed for freelancers and remote workers.
            </p>

            <div className="mt-7 space-y-1.5">
              <p className="aug-label text-[var(--theme-muted)]">
                <span className="text-[var(--theme-fg)]">Bilitech LLC</span> · 30 N Gould St Ste N,
                Sheridan, WY 82801
              </p>
              <p className="aug-label text-[var(--theme-fg)]">VCC Africa Company Limited</p>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-7">
              {socialLinks.map(({ href, label, icon: Icon }) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-10 h-10 rounded-full border border-[var(--theme-line)] flex items-center justify-center text-[var(--theme-muted)] transition-colors duration-300 hover:text-[var(--color-black)] hover:bg-[var(--color-accent)] hover:border-[var(--color-accent)]"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {columns.map((col, i) => (
            <div key={col.title}>
              <p className="aug-label text-[var(--theme-muted)] pb-4 mb-2 border-b border-[var(--theme-line)]">
                (0{i + 1}) — {col.title}
              </p>
              <div className="flex flex-col items-start">
                {col.links.map((l) => (
                  <FooterLink key={l.to} to={l.to}>
                    {l.label}
                  </FooterLink>
                ))}
              </div>

              {/* Store badges live under the last column */}
              {i === columns.length - 1 && (
                <div className="mt-8">
                  <p className="aug-label text-[var(--theme-muted)] pb-4 mb-4 border-b border-[var(--theme-line)]">
                    (04) — Get the app
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="https://apps.apple.com/us/app/yunlen/id6751148625"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="transition-transform duration-300 hover:-translate-y-0.5"
                    >
                      <img src="/storeImages/AppStore.webp" alt="Download on the App Store" className="h-11 w-auto" />
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.yunlen.app"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="transition-transform duration-300 hover:-translate-y-0.5"
                    >
                      <img src="/storeImages/Playstore.webp" alt="Get it on Google Play" className="h-11 w-auto" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact strip */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-10 py-6 border-t border-[var(--theme-line)]">
          <a href="mailto:contact@yunlen.app" className="aug-link aug-label">
            <Mail className="w-4 h-4" />
            contact@yunlen.app
          </a>
          <a href="tel:+13072023238" className="aug-link aug-label">
            <Phone className="w-4 h-4" />
            +1 (307) 202-3238
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=com.yunlen.app"
            target="_blank"
            rel="noopener noreferrer"
            className="aug-link aug-label sm:ml-auto"
          >
            Get started
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>

        {/* Legal strip */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 py-6 border-t border-[var(--theme-line)]">
          <p className="aug-label text-[var(--theme-muted)]">
            © {currentYear} Bilitech LLC &amp; VCC Africa Company Limited. All rights reserved.
          </p>
          <p className="text-[11px] leading-relaxed text-[var(--grey-600)] max-w-2xl">
            Third-party logos (e.g. Amazon, Meta, Visa, PayPal, Google Play, App Store) are
            trademarks of their respective owners. YUNLEN is not affiliated with, endorsed by, or
            sponsored by these companies.
          </p>
        </div>
      </div>

      {/* Oversized wordmark, cropped by the footer's bottom edge */}
      <div className="relative select-none" aria-hidden="true">
        <div className="mx-auto w-full max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <span className="aug-wordmark block text-[var(--grey-900)] translate-y-[14%]">
            Yunlen
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
